import type { AxiosInstance } from "axios";
import type { ReportOutput, ReportRow, ReportRowsPage } from "./types";
import { reportPath } from "./utils";

/**
 * A detail dataset's rows, read a page at a time. A page that does not move
 * the offset forward is an error rather than another turn of the loop.
 */
const readDetailRows = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  dataset: string,
  signal: AbortSignal,
): Promise<ReportRow[]> => {
  const rows: ReportRow[] = [];
  let offset = 0;
  for (;;) {
    signal.throwIfAborted();
    const { data } = await api.get<ReportRowsPage>(
      `${reportPath(businessId, runId)}/rows`,
      { params: { dataset, offset }, signal },
    );
    rows.push(...data.rows);
    if (data.next_offset === null) return rows;
    if (data.next_offset <= offset)
      throw new Error("The report's rows could not be read.");
    offset = data.next_offset;
  }
};

/**
 * The run's output with every dataset's rows. The API sends a detail
 * dataset without them; they are read here, so the rest of the page reads a
 * dataset the same way whatever its size.
 */
export const readReportOutput = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal: AbortSignal,
): Promise<ReportOutput> => {
  const { data } = await api.get<{ output: ReportOutput }>(
    `${reportPath(businessId, runId)}/output`,
    { signal },
  );
  const { output } = data;
  if (!output.datasets?.some((dataset) => dataset.detail)) return output;

  const datasets = await Promise.all(
    output.datasets.map(async (dataset) =>
      dataset.detail
        ? {
            ...dataset,
            rows: await readDetailRows(
              api,
              businessId,
              runId,
              dataset.id,
              signal,
            ),
          }
        : dataset,
    ),
  );
  return { ...output, datasets };
};
